import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import { monitoringDetectedApi } from '~/apis';
import { useGetFilterParams } from '~/components/Filters/@hooks';
import { useClientBrandIds } from '~/hooks';
import {
  MarqBrandCoreModelClientListingSortedColumnEnum,
  MarqBrandEnumsListingMonitoringStatusEnum,
  MarqCommonEnumsChannelTypeEnum,
  MarqCommonEnumsSortedOrderEnum,
  MarqFoundationEnumsCurrencyEnum,
  MonitoringCommonViewProtocolsGetAllListingIdRequest,
  MonitoringCommonViewProtocolsGetCountRequest,
  MonitoringCommonViewProtocolsGetCountResponse,
  MonitoringImageViewProtocolsGetImageGroupsRequest,
  MonitoringImageViewProtocolsGetImageGroupsResponse,
  MonitoringListViewProtocolsGetListingsRequest,
  MonitoringSellerViewProtocolsGetListingIdForSellerRequest,
  MonitoringSellerViewProtocolsGetSellerDetailsRequest,
  MonitoringSellerViewProtocolsGetSellerDetailsResponse,
  MonitoringSellerViewProtocolsGetSellerListingsRequest,
  MonitoringSellerViewProtocolsGetSellerListingsResponse,
  MonitoringSellerViewProtocolsGetSellersRequest,
} from '~/swagger/data-contracts';
import { IListing, TListingSortedColumn, TSellerSortedColumn } from '~/types/detected';

export const QueryKeyDetected = 'detected';

/**
 * @deprecated
 */
export type SortOrder = 'asc' | 'desc';

export type EnforcementResponse = {
  affected: number;
  success: boolean;
  listing_image_list?: string[] | null;
  listing_title?: string | null;
  potential_authorized_count: number;
  request_id?: number | null;
};

interface IGetListingRequest {
  asset_id_filter: number[];
  client_requested_filter?: boolean;
  client_status_filter?: MarqBrandEnumsListingMonitoringStatusEnum;
  currency?: MarqFoundationEnumsCurrencyEnum;
  channel_type: MarqCommonEnumsChannelTypeEnum[];
  flag_id_filter: number[];
  domain_id_filter: number[];
  max_price?: number;
  min_price?: number;
  official_image_id_filter: number[];
  repeat_offender_filter?: boolean;
  search?: string;
  seller_name_filter: string[];
  top_result_filter?: boolean;
  tm_infringement_filter?: boolean;
  tm_infringement_logo_filter?: number[];
  tm_infringement_text_filter?: boolean;
  infringing_images_filter?: boolean;
  low_price_filter?: boolean;
  gen_ai_counterfeit_filter?: boolean;
  model_id_filter: number[];
  custom_tag_id_filter: number[];
  ship_from_filter: string[];
}

export interface IGetDetectedListRequest extends IGetListingRequest {
  brand_id: number | null;
  limit: number;
  offset: number;
  sorted_column: TListingSortedColumn;
  sorted_order: SortOrder;
  search_date_filter: Dayjs;
}

export interface IGetImageViewDetectedListRequest extends IGetListingRequest {
  brand_id: number | null;
  limit: number;
  offset: number;
  sorted_column?: 'group_size';
  sorted_order?: MarqCommonEnumsSortedOrderEnum;
  search_date_filter: Dayjs;
}

export interface IGetSellerDetectedListRequest extends Omit<IGetDetectedListRequest, 'search_date_filter'> {
  seller_sorted_column: TSellerSortedColumn;
  seller_sorted_order: SortOrder;
}

export interface IGetDetectedListResponse {
  asset_exists: boolean;
  listings: IListing[];
}

export interface IGetDetectedTotalResponse {
  total_count: number;
}

export interface IGetSellerCountRequest extends IGetListingRequest {
  seller_sorted_column: TSellerSortedColumn;
  seller_sorted_order: SortOrder;
  limit: number;
  offset: number;
}

export interface IGetSellerListingRequest extends IGetListingRequest {
  seller_id: number | null;
  sorted_column: TListingSortedColumn;
  sorted_order: SortOrder;
}

export interface ISellerListing {
  domain_id: number;
  domain_name: string;
  listing_list: IListing[];
  seller_page_url: string;
  snapshot_path: string;
  total_count: number;
}

export interface ISellerDetail {
  seller_id: string;
  business_reg_number: string;
  clustered: boolean;
  company_address: string;
  company_name: string;
  denied_listing_count: number;
  domain_id: number;
  domain_name: string;
  email: string;
  online_market_number: string;
  other_info: string;
  phone_number: string;
  repeat_offender: boolean;
  representative_name: string;
  seller_name: string;
  denied_count: number;
  detected_count: number;
  enforced_count: number;
  investigated_count: number;
  not_reportable_count: number;
  on_standby_count: number;
  waiting_investigation_count: number;
  waiting_report_count: number;
}

export const fetchGetAllListingId = (request: MonitoringCommonViewProtocolsGetAllListingIdRequest) => {
  return monitoringDetectedApi.getListingIdsCreate(request);
};

export const fetchGetSellerAllListingId = (request: MonitoringSellerViewProtocolsGetListingIdForSellerRequest) => {
  return monitoringDetectedApi.sellerViewGetSellerListingIdsCreate(request);
};

export const useListTotalCountQuery = (
  request: MonitoringCommonViewProtocolsGetCountRequest,
  {
    enabled = true,
    handleSuccess,
  }: { enabled?: boolean; handleSuccess?: (data: MonitoringCommonViewProtocolsGetCountResponse) => void } = {}
): UseQueryResult<MonitoringCommonViewProtocolsGetCountResponse, unknown> => {
  const { brandId } = useClientBrandIds();
  const { legacyRequestFilterParams } = useGetFilterParams();
  return useQuery(
    [QueryKeyDetected, 'count', { ...request, ...legacyRequestFilterParams }],
    async () => {
      const { data } = await monitoringDetectedApi.countCreate({
        ...request,
        ...legacyRequestFilterParams,
        brand_id: brandId,
        currency: request.currency as MarqFoundationEnumsCurrencyEnum,
      });
      return data;
    },
    { enabled, onSuccess: handleSuccess }
  );
};

export const useReviewList = (request: IGetDetectedListRequest) => {
  const { brandId } = useClientBrandIds();
  const { legacyRequestFilterParams } = useGetFilterParams();

  return useReviewListQuery({
    ...request,
    ...legacyRequestFilterParams,
    brand_id: brandId,
    search_date_filter: request.search_date_filter.toISOString(),
    sorted_column: request.sorted_column as MarqBrandCoreModelClientListingSortedColumnEnum,
    sorted_order: request.sorted_order as MarqCommonEnumsSortedOrderEnum,
    currency: request.currency as MarqFoundationEnumsCurrencyEnum,
  });
};

export const useReviewListQuery = (request: MonitoringListViewProtocolsGetListingsRequest, { enabled = true } = {}) => {
  return useInfiniteQuery(
    [QueryKeyDetected, 'list', request],
    async ({ pageParam }) => {
      const { data } = await monitoringDetectedApi.listViewGetListingsCreate({
        ...request,
        offset: pageParam || request.offset,
      });
      return data;
    },
    {
      enabled,
      getNextPageParam: (lastPage, allPage) => {
        if (lastPage.listings.length < 100) {
          return;
        }

        return request.limit * allPage.length;
      },
    }
  );
};

//>>>
export const useSellerCountQuery = (request: MonitoringSellerViewProtocolsGetSellersRequest) => {
  return useInfiniteQuery(
    [QueryKeyDetected, 'seller-count', request],
    async ({ pageParam }) => {
      const { data } = await monitoringDetectedApi.sellerViewGetSellersCreate({
        ...request,

        offset: pageParam || request.offset,
      });
      return data;
    },
    {
      getNextPageParam: (lastPage, allPage) => {
        if (lastPage.sellers.length < 100) {
          return;
        }

        return request.limit * allPage.length;
      },
    }
  );
};
export const useSellerListingQuery = (
  request: MonitoringSellerViewProtocolsGetSellerListingsRequest,
  options?: UseQueryOptions<MonitoringSellerViewProtocolsGetSellerListingsResponse, unknown>
) => {
  return useQuery({
    queryKey: [QueryKeyDetected, 'seller-listing', request.seller_id, request],
    queryFn: async () => {
      const { data } = await monitoringDetectedApi.sellerViewGetListingsCreate(request);
      return data;
    },
    ...options,
  });
};
export const useSellerDetailQuery = (
  request: MonitoringSellerViewProtocolsGetSellerDetailsRequest,
  options?: UseQueryOptions<MonitoringSellerViewProtocolsGetSellerDetailsResponse, unknown>
) => {
  return useQuery({
    queryKey: [QueryKeyDetected, 'seller-detail', request],
    queryFn: async () => {
      const { data } = await monitoringDetectedApi.sellerViewSellerDetailCreate(request);
      return data;
    },
    ...options,
  });
};
///<<<<

export const useImageViewListQuery = (
  request: MonitoringImageViewProtocolsGetImageGroupsRequest
): UseInfiniteQueryResult<MonitoringImageViewProtocolsGetImageGroupsResponse, unknown> => {
  return useInfiniteQuery(
    [QueryKeyDetected, 'list', request],
    async ({ pageParam }) => {
      const { data } = await monitoringDetectedApi.imageViewGetGroupsCreate({
        ...request,
        offset: pageParam || request.offset,
      });
      return data;
    },
    {
      getNextPageParam: (lastPage, allPage) => {
        if (lastPage.listing_groups.length < 20) {
          return;
        }

        return request.limit * allPage.length;
      },
    }
  );
};
export const useImageViewGetMoreListingMutation = () => useMutation(monitoringDetectedApi.imageViewGetListingsCreate);
export const useImageViewGetGroupListingIdMutation = () =>
  useMutation(monitoringDetectedApi.imageViewGetGroupListingIdsCreate);

export const useDetectionExcelExportMutation = () => useMutation(monitoringDetectedApi.excelExportCreate);
export const useDetectionListingIdExcelExportMutation = () =>
  useMutation(monitoringDetectedApi.excelExportListingIdCreate);
